import { Reducer } from 'redux';
import {
	EXERCISES_CLEAR_ANSWERS,
	EXERCISES_REMOVE_ANSWER,
	EXERCISES_SET_ANSWER,
	EXERCISES_SET_DATA,
	EXERCISES_SET_LOAD_FAILED,
	EXERCISES_SET_EXERCISE,
	EXERCISES_SET_GENERATING,
	EXERCISES_SET_STUDENTS,
	EXERCISES_SET_STATUS,
	EXERCISES_SET_GENERATE_FAILED,
	EXERCISES_SET_NATIVE,
	IExercisesState,
	TExercisesActions,
} from './types';
import { getExerciseGeneratingKey } from './selectors';

const initialState: IExercisesState = {
	byAudioId: {},
	generating: {},
	generateFailed: {},
	loadFailed: {},
	native: {},
};

export const exercisesReducer: Reducer<IExercisesState, TExercisesActions> = (
	state = initialState,
	action
) => {
	switch (action.type) {
		case EXERCISES_SET_DATA: {
			// preserve students/studentAnswers set by a concurrent loadStudents() call — fetch() doesn't return them
			const current = state.byAudioId[action.audioId];
			return {
				...state,
				byAudioId: {
					...state.byAudioId,
					[action.audioId]: { ...action.data, students: current?.students, studentAnswers: current?.studentAnswers },
				},
			};
		}

		case EXERCISES_SET_GENERATING: {
			const key = getExerciseGeneratingKey(action.audioId, action.promptId);
			return {
				...state,
				generating: { ...state.generating, [key]: action.generating },
			};
		}

		case EXERCISES_SET_EXERCISE: {
			const current = state.byAudioId[action.audioId];
			if (!current) return state;
			const exercises = [
				...current.exercises.filter(e => e.promptId !== action.exercise.promptId),
				action.exercise,
			];
			// a (re)generated exercise invalidates any answers already held for its id — the server
			// deletes every student's answers on regenerate too, so the cached teacher view must match
			const myAnswers = current.myAnswers.filter(a => a.exerciseId !== action.exercise.id);
			const studentAnswers = current.studentAnswers?.filter(a => a.exerciseId !== action.exercise.id);
			return {
				...state,
				byAudioId: { ...state.byAudioId, [action.audioId]: { ...current, exercises, myAnswers, studentAnswers } },
			};
		}

		case EXERCISES_SET_ANSWER: {
			const current = state.byAudioId[action.audioId];
			if (!current) return state;
			const myAnswers = [
				...current.myAnswers.filter(a => !(a.exerciseId === action.answer.exerciseId && a.questionIndex === action.answer.questionIndex)),
				action.answer,
			];
			return {
				...state,
				byAudioId: { ...state.byAudioId, [action.audioId]: { ...current, myAnswers } },
			};
		}

		case EXERCISES_REMOVE_ANSWER: {
			const current = state.byAudioId[action.audioId];
			if (!current) return state;
			const myAnswers = current.myAnswers.filter(a => !(a.exerciseId === action.exerciseId && a.questionIndex === action.questionIndex));
			return {
				...state,
				byAudioId: { ...state.byAudioId, [action.audioId]: { ...current, myAnswers } },
			};
		}

		case EXERCISES_CLEAR_ANSWERS: {
			const current = state.byAudioId[action.audioId];
			if (!current) return state;
			const myAnswers = current.myAnswers.filter(a => a.exerciseId !== action.exerciseId);
			return {
				...state,
				byAudioId: { ...state.byAudioId, [action.audioId]: { ...current, myAnswers } },
			};
		}

		case EXERCISES_SET_STUDENTS: {
			// may arrive before the exercises fetch resolves — seed a placeholder entry rather than dropping it
			const current = state.byAudioId[action.audioId] || { types: [], exercises: [], myAnswers: [], canRegenerate: false };
			return {
				...state,
				byAudioId: {
					...state.byAudioId,
					[action.audioId]: { ...current, students: action.students, studentAnswers: action.answers },
				},
			};
		}

		case EXERCISES_SET_LOAD_FAILED:
			return {
				...state,
				loadFailed: { ...state.loadFailed, [action.audioId]: action.failed },
			};

		case EXERCISES_SET_STATUS: {
			const current = state.byAudioId[action.audioId];
			if (!current) return state;
			const exercises = current.exercises.map(e => e.id === action.exerciseId ? { ...e, status: action.status } : e);
			return {
				...state,
				byAudioId: { ...state.byAudioId, [action.audioId]: { ...current, exercises } },
			};
		}

		case EXERCISES_SET_GENERATE_FAILED: {
			const key = getExerciseGeneratingKey(action.audioId, action.promptId);
			return {
				...state,
				generateFailed: { ...state.generateFailed, [key]: action.failed },
			};
		}

		case EXERCISES_SET_NATIVE:
			return {
				...state,
				native: { ...state.native, [action.exerciseId]: action.native },
			};

		default:
			return state;
	}
};
